// ============================================================================
// Ritual Type Types (matches backend)
// ============================================================================

export enum RitualTypeEnum {
  // New types (primary)
  PUBLIC = "PUBLIC",
  PRIVATE = "PRIVATE",
  PERSONAL = "PERSONAL",
  // Legacy types (for backward compatibility with existing data)
  IN_PERSON = "IN_PERSON",
  VIRTUAL_COURSE = "VIRTUAL_COURSE",
  SELF_GUIDED = "SELF_GUIDED",
  SELF_GUIDED_COURSE = "SELF_GUIDED_COURSE",
}

// ============================================================================
// Ritual Visibility Types (Public/Private/Personal)
// ============================================================================

export enum RitualVisibilityEnum {
  PUBLIC = "PUBLIC",
  PRIVATE = "PRIVATE",
  PERSONAL = "PERSONAL",
}

export type RitualVisibilityValue = `${RitualVisibilityEnum}`

// Display labels for visibility
export type RitualVisibilityLabel =
  | "Public Ritual"
  | "Private Ritual"
  | "Personal Ritual (only for me)"

// Complete mapping object for visibility
export const RITUAL_VISIBILITY_MAP: Record<
  RitualVisibilityEnum,
  { label: RitualVisibilityLabel; description: string }
> = {
  [RitualVisibilityEnum.PUBLIC]: {
    label: "Public Ritual",
    description: "Visible to everyone on the platform",
  },
  [RitualVisibilityEnum.PRIVATE]: {
    label: "Private Ritual",
    description: "Visible only to invited members",
  },
  [RitualVisibilityEnum.PERSONAL]: {
    label: "Personal Ritual (only for me)",
    description: "Visible only to you",
  },
}

// Select options for visibility form
export const RITUAL_VISIBILITY_OPTIONS: Array<{
  value: RitualVisibilityEnum
  label: RitualVisibilityLabel
  description: string
}> = [
  {
    value: RitualVisibilityEnum.PUBLIC,
    label: "Public Ritual",
    description: "Visible to everyone on the platform",
  },
  {
    value: RitualVisibilityEnum.PRIVATE,
    label: "Private Ritual",
    description: "Visible only to invited members",
  },
  {
    value: RitualVisibilityEnum.PERSONAL,
    label: "Personal Ritual (only for me)",
    description: "Visible only to you",
  },
]

// Helper function to get visibility label
export function getRitualVisibilityLabel(visibility: string): RitualVisibilityLabel | string {
  return RITUAL_VISIBILITY_MAP[visibility as RitualVisibilityEnum]?.label || visibility
}

export type RitualTypeValue = `${RitualTypeEnum}`

// Format values for UI components
export type RitualTypeFormat =
  | "public"
  | "private"
  | "personal"
  | "in-person"
  | "virtual-course"
  | "self-guided"
  | "self-guided-course"

// Display labels
export type RitualTypeLabel =
  | "Public"
  | "Private"
  | "Personal"
  | "In Person"
  | "Live Virtual"
  | "Self Guided"
  | "Self Guided Course"

// Complete mapping object
export const RITUAL_TYPE_MAP: Record<
  RitualTypeEnum,
  { format: RitualTypeFormat; label: RitualTypeLabel }
> = {
  // New types
  [RitualTypeEnum.PUBLIC]: { format: "public", label: "Public" },
  [RitualTypeEnum.PRIVATE]: { format: "private", label: "Private" },
  [RitualTypeEnum.PERSONAL]: { format: "personal", label: "Personal" },
  // Legacy types (for backward compatibility)
  [RitualTypeEnum.IN_PERSON]: { format: "in-person", label: "In Person" },
  [RitualTypeEnum.VIRTUAL_COURSE]: { format: "virtual-course", label: "Live Virtual" },
  [RitualTypeEnum.SELF_GUIDED]: { format: "self-guided", label: "Self Guided" },
  [RitualTypeEnum.SELF_GUIDED_COURSE]: {
    format: "self-guided-course",
    label: "Self Guided Course",
  },
}

// Helper functions
export function getRitualTypeFormat(type: string): RitualTypeFormat | string {
  return RITUAL_TYPE_MAP[type as RitualTypeEnum]?.format || type.toLowerCase()
}

export function getRitualTypeLabel(type: string): RitualTypeLabel | string {
  return RITUAL_TYPE_MAP[type as RitualTypeEnum]?.label || type
}

// Select options for forms (legacy types - for ritual type selector in UI)
export const RITUAL_TYPE_OPTIONS: Array<{ value: RitualTypeEnum; label: RitualTypeLabel }> = [
  { value: RitualTypeEnum.IN_PERSON, label: "In Person" },
  { value: RitualTypeEnum.VIRTUAL_COURSE, label: "Live Virtual" as RitualTypeLabel },
  { value: RitualTypeEnum.SELF_GUIDED, label: "Self Guided" },
  { value: RitualTypeEnum.SELF_GUIDED_COURSE, label: "Self Guided Course" },
]

// All types including legacy (for backward compatibility)
export const RITUAL_TYPE_OPTIONS_ALL: Array<{ value: RitualTypeEnum; label: RitualTypeLabel }> = [
  { value: RitualTypeEnum.PUBLIC, label: "Public" },
  { value: RitualTypeEnum.PRIVATE, label: "Private" },
  { value: RitualTypeEnum.PERSONAL, label: "Personal" },
  { value: RitualTypeEnum.IN_PERSON, label: "In Person" },
  { value: RitualTypeEnum.VIRTUAL_COURSE, label: "Live Virtual" as RitualTypeLabel },
  { value: RitualTypeEnum.SELF_GUIDED, label: "Self Guided" },
  { value: RitualTypeEnum.SELF_GUIDED_COURSE, label: "Self Guided Course" },
]

// Backward compatibility alias
export const formatRitualType = getRitualTypeLabel

// ============================================================================
// Legacy Ritual Type (for fee type)
// ============================================================================

export enum RitualFeeType {
  FREE = "free",
  PAID = "paid",
}

export enum RitualCategory {
  MEDITATION = "meditation",
  YOGA = "yoga",
  WORKSHOP = "workshop",
  CEREMONY = "ceremony",
  GATHERING = "gathering",
  RETREAT = "retreat",
  OTHER = "other",
}

export enum RitualFrequency {
  ONE_TIME = "one_time",
  DAILY = "daily",
  WEEKLY = "weekly",
  MONTHLY = "monthly",
  CUSTOM = "custom",
}

export enum RitualLocationType {
  VIRTUAL = "virtual",
  IN_PERSON = "in_person",
  HYBRID = "hybrid",
}

export interface RitualFormData {
  // Info section
  title: string
  type: RitualLocationType
  dateTime: string
  repeat: string
  duration: string
  address?: string
  city?: string
  state?: string
  country?: string
  lat?: number
  long?: number
  meetingLink?: string
  steps?: string
  participationFee: "free" | "paid"
  price?: number
  currency?: string
  feeDetails?: string

  // Details section
  description: string
  whatToBring?: string
  whatYoullExperience?: string
  intention?: string
  intentions?: Array<{ id: string; name: string }>
  websiteOrSocialMedia?: string
  tags: string[]

  // Organizers
  organizers: Array<{
    userId: string
    userName: string
    userAvatar?: string
    roleId: string
    roleName: string
  }>

  // Media
  mediaFiles: {
    images: Array<{ id: string; file: File; url: string; isCover: boolean }>
    videos: Array<{ id: string; file: File; url: string }>
  }

  // Privacy
  privacySettings: {
    visibility: string[]
    comment: string[]
    edit: string[]
    join: string[]
  }
}

export const INVITE_NOTE_MAX_LENGTH = 250

export interface SelectOption {
  value: string
  label: string
}

export interface Organizer {
  id: string // user ID or community ID
  type: "user" | "community"
  name: string
  identifier: string | null // username or handle
  avatar?: string
  roleId: string
  roleName: string
}
